// src/pages/DashboardPage.jsx
import { useEffect, useState } from 'react';
import { useAuth } from '../hooks/useAuth';
import { getSalesSummary, getOrders } from '../lib/db';

const fmt = (n) => '$' + (n || 0).toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export function DashboardPage({ setPage }) {
  const { shopId, shop } = useAuth();
  const [summary,  setSummary]  = useState([]);
  const [recent,   setRecent]   = useState([]);
  const [loading,  setLoading]  = useState(true);

  useEffect(() => {
    if (!shopId) { setLoading(false); return; }
    Promise.all([
      getSalesSummary(shopId),
      getOrders(shopId, { limit: 8 })
    ]).then(([s, r]) => {
      setSummary(s);
      setRecent(r);
    }).finally(() => setLoading(false));
  }, [shopId]);

  const totals = summary.reduce((acc, row) => ({
    today: { count: acc.today.count + row.today.count, total: acc.today.total + row.today.total },
    month: { count: acc.month.count + row.month.count, total: acc.month.total + row.month.total },
    year:  { count: acc.year.count  + row.year.count,  total: acc.year.total  + row.year.total  },
  }), { today:{count:0,total:0}, month:{count:0,total:0}, year:{count:0,total:0} });

  const now = new Date();
  const dateStr = now.toLocaleDateString('en-CA', { weekday:'long', year:'numeric', month:'long', day:'numeric' });

  if (loading) return <div className="loading-center"><div className="spinner"/></div>;

  return (
    <div>
      {/* Header */}
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:20}}>
        <div>
          <h1 style={{color:'var(--g800)'}}>{shop?.name}</h1>
          <p style={{color:'var(--text-muted)',fontSize:13,marginTop:3}}>{dateStr}</p>
        </div>
        <button className="btn btn-primary" onClick={()=>setPage('new-order')}>
          ➕ New Order
        </button>
      </div>

      {/* Stat cards */}
      <div className="stat-grid">
        <div className="stat-card">
          <div className="stat-label">Today's Revenue</div>
          <div className="stat-value">{fmt(totals.today.total)}</div>
          <div className="stat-sub">{totals.today.count} orders</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Month to Date</div>
          <div className="stat-value">{fmt(totals.month.total)}</div>
          <div className="stat-sub">{totals.month.count} orders</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Year to Date</div>
          <div className="stat-value">{fmt(totals.year.total)}</div>
          <div className="stat-sub">{totals.year.count} orders</div>
        </div>
        <div className="stat-card" style={{borderLeft:'3px solid var(--g400)'}}>
          <div className="stat-label">Avg Order Value</div>
          <div className="stat-value">
            {fmt(totals.year.count ? totals.year.total / totals.year.count : 0)}
          </div>
          <div className="stat-sub">this year</div>
        </div>
      </div>

      {/* Sales breakdown table */}
      <div className="card">
        <div className="card-header">
          <span className="card-title">Sales Summary</span>
          <button className="btn btn-ghost btn-sm" onClick={()=>setPage('reports')}>Full Report →</button>
        </div>
        <div className="table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <th>Order Type</th>
                <th>Today #</th><th>Today $</th>
                <th>MTD #</th><th>MTD $</th>
                <th>YTD #</th><th>YTD $</th>
              </tr>
            </thead>
            <tbody>
              {summary.map(row => (
                <tr key={row.type}>
                  <td style={{fontWeight:600}}>{row.label}</td>
                  <td>{row.today.count}</td>
                  <td>{fmt(row.today.total)}</td>
                  <td>{row.month.count}</td>
                  <td>{fmt(row.month.total)}</td>
                  <td>{row.year.count}</td>
                  <td style={{fontWeight:600,color:'var(--g700)'}}>{fmt(row.year.total)}</td>
                </tr>
              ))}
              <tr style={{background:'var(--g50)'}}>
                <td style={{fontWeight:700}}>Totals</td>
                <td style={{fontWeight:700}}>{totals.today.count}</td>
                <td style={{fontWeight:700}}>{fmt(totals.today.total)}</td>
                <td style={{fontWeight:700}}>{totals.month.count}</td>
                <td style={{fontWeight:700}}>{fmt(totals.month.total)}</td>
                <td style={{fontWeight:700}}>{totals.year.count}</td>
                <td style={{fontWeight:800,color:'var(--g700)',fontSize:15}}>{fmt(totals.year.total)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* Recent orders */}
      <div className="card">
        <div className="card-header">
          <span className="card-title">Recent Orders</span>
          <button className="btn btn-ghost btn-sm" onClick={()=>setPage('orders')}>View all →</button>
        </div>
        {recent.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">💐</div>
            <h3>No orders yet</h3>
            <p>Create your first order to see it here.</p>
          </div>
        ) : (
          <div className="table-wrap">
            <table className="data-table">
              <thead>
                <tr><th>#</th><th>Customer</th><th>Type</th><th>Total</th><th>Status</th><th>Date</th></tr>
              </thead>
              <tbody>
                {recent.map(o => (
                  <tr key={o.id}>
                    <td style={{fontWeight:600,color:'var(--g700)'}}>#{o.orderNumber || '—'}</td>
                    <td>{o.customerName || o.billingFirstName + ' ' + o.billingLastName || '—'}</td>
                    <td><span className="pill pill-gray">{TYPE_LABEL[o.type] || o.type}</span></td>
                    <td style={{fontWeight:600}}>{fmt(o.total)}</td>
                    <td><StatusPill status={o.status}/></td>
                    <td style={{color:'var(--text-muted)',fontSize:12.5}}>{fmtDate(o.createdAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

const TYPE_LABEL = {
  carry_out:'Carry Out', local_delivery:'Local Delivery',
  pick_up:'Pick Up', wire_out:'Wire Out', wire_in:'Wire In'
};

function StatusPill({ status }) {
  const map = {
    pending:   ['pill-gold',  'Pending'],
    completed: ['pill-green', 'Completed'],
    paid:      ['pill-green', 'Paid'],
    draft:     ['pill-gray',  'Draft'],
    void:      ['pill-red',   'Void'],
    ready:     ['pill-blue',  'Ready'],
  };
  const [cls, label] = map[status] || ['pill-gray', status];
  return <span className={`pill ${cls}`}>{label}</span>;
}

function fmtDate(ts) {
  if (!ts) return '—';
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return d.toLocaleDateString('en-CA', { month:'short', day:'numeric', hour:'2-digit', minute:'2-digit' });
}
